import styled from 'styled-components';

import { Text } from '../../../../../components';
import { COLORS, MOBILE, SPACE } from '../../../../../theme/tokens';

/** The leading column: the icon's box (Figma 56:2672, a 24px square holding a 16px glyph). The eye
 *  button is exactly this square. */
const LEADING_PX = 24;
/** Each row's height (Figma 56:2671 / 56:2707): the copy button's 26px box sets it. */
const ROW_PX = 26;

/**
 * The inset credential block: the two things an operator copies into another application, on
 * their own ground. Icon-led rows with a monospaced value and its copy control right after it. Same
 * shape as the house credential blocks, written here so this scenario shares nothing by import.
 */
export const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
`;

/**
 * One credential row: icon, value (as wide as it is, no wider), its copy button right after it, then
 * the far-right column (Change password on the password row, empty on the address row). Named for
 * assistive tech via role/aria-label on the element, since the icon carries no text. The value
 * ellipsizes (min-width: 0) so its copy always stays beside it; only the link wraps under MOBILE.
 */
export const Row = styled.div`
  display: grid;
  /* ONE grid for both rows: a FIXED leading column (the icon's box, never a button's padding), so the
     mail icon and the eye start at the same x and the two values line up at x = 36. The value column
     grows only to its content, so the copy follows it; the last column takes what is left. */
  grid-template-columns: ${LEADING_PX}px minmax(0, max-content) auto minmax(0, 1fr);
  align-items: center;
  min-height: ${ROW_PX}px;
  gap: ${SPACE[12]};

  ${MOBILE} {
    row-gap: ${SPACE[8]};
  }
`;

export const RowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LEADING_PX}px;
  height: ${LEADING_PX}px;
  color: ${COLORS.iconBright};
`;

/**
 * Monospaced, selectable machine value: the address and the revealed password. Raw data an
 * operator copies into a mail client, so rendered directly rather than through the i18n <Text>,
 * and monospaced because it is read character by character (`l` vs `1`, `O` vs `0`).
 */
export const Mono = styled.code`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  color: ${COLORS.textStrong};
`;

/** The masked password: dots in the value's own column, so the eye toggles nothing else. */
export const MaskedDots = styled.span`
  min-width: 0;
  overflow: hidden;
  letter-spacing: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  color: ${COLORS.textFaint};
`;

/**
 * The eye: an icon-only button in the leading column, EXACTLY the column's square with zero padding,
 * so its glyph starts at the same x as the mail icon's. The comfortable hit area comes back through
 * an inset pseudo-element, never through padding (padding is what pushed the row out of line).
 * Named by its aria-label, pressed state exposed, visible focus ring.
 */
export const EyeToggle = styled.button`
  position: relative;
  appearance: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LEADING_PX}px;
  height: ${LEADING_PX}px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  color: ${COLORS.textMuted};

  /* The hit area: a 36px square around the 24px box, outside the layout. */
  &::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  &:hover {
    color: ${COLORS.textStrong};
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }

  /* No plaintext to reveal: the glyph stays, faint, and the title says why. */
  &:disabled {
    cursor: not-allowed;
    color: ${COLORS.textFaint};
    background: none;
  }
`;

/** Generate / Change password: a text link in the CTA colour, alone at the far right of
 *  the password row. */
export const ActionLink = styled.button`
  appearance: none;
  justify-self: end;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.ctaSolid};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:disabled {
    cursor: default;
    color: ${COLORS.textFaint};
  }

  /* Under MOBILE it drops to its own line under the value, flush with it: the value and its copy are
     never squeezed to make room for the link. */
  ${MOBILE} {
    grid-column: 2 / -1;
    justify-self: start;
  }
`;

/** A hairline between the address row and the password row, and nowhere else in the block: full
 *  width of the content box. The block sits on the inset surface, one step above the card, and the
 *  panel-border lift vanished against it; `surfaceMuted` is the next step up (the same token that
 *  outlines a deleted tile), so the line reads without a raw colour. */
export const Divider = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0;
  border: 0;
  background: ${COLORS.surfaceMuted};
`;

/** Under the password row, only while the plaintext is held: it is shown ONCE and stored nowhere,
 *  so the block says so plainly rather than in a tooltip. */
export const OnceNote = styled(Text)`
  display: block;
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: ${COLORS.textMuted};
`;
